import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Pencil,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  EmptyState,
  LoadingState,
  PageHeader,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui';
import {
  describeApiError,
  describePublishFailReason,
  getApiErrorCode,
  translateErrorCode,
} from '@/lib/api-errors';
import type { ReviewActionItem, ReviewQueueItem } from '@/api/review';
import {
  useApproveReviewDesigns,
  useDiscardReviewDesigns,
  useRetryReviewDesigns,
  useReviewJob,
  useReviewQueue,
} from '@/hooks/useReviewQueue';
import { formatDate } from '@/lib/format-date';

/**
 * Thumbnail column: finished mockups, an explicit placeholder while renders
 * are pending, the design thumbnail when no mockup exists at all, and an
 * empty tile when nothing is available yet.
 */
function MockupStrip({ item }: { item: ReviewQueueItem }) {
  const { t } = useTranslation();
  const completed = item.mockups
    .filter((mockup) => mockup.status === 'COMPLETED' && mockup.imageUrl)
    .slice(0, 4);
  const showThumbnail = item.mockupSummary.total === 0 && item.thumbnailUrl;
  const showEmptyTile =
    completed.length === 0 &&
    item.mockupSummary.pending === 0 &&
    !showThumbnail;

  return (
    <div className="flex items-center gap-2">
      {completed.map((mockup, index) => (
        <img
          key={`${mockup.positionSlug}-${index}`}
          src={mockup.imageUrl as string}
          alt={t('seller.review.item.mockupAlt', { name: item.name })}
          className="h-11 w-11 rounded-chip bg-paper object-contain ring-1 ring-line"
          loading="lazy"
        />
      ))}
      {item.mockupSummary.pending > 0 && (
        <div
          className="flex h-11 w-11 flex-col items-center justify-center rounded-chip bg-paper text-mute ring-1 ring-line"
          title={t('seller.review.item.pendingHint')}
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span className="mt-0.5 font-mono text-[10px]">
            {t('seller.review.item.pending', {
              count: item.mockupSummary.pending,
            })}
          </span>
        </div>
      )}
      {showThumbnail && (
        <img
          src={item.thumbnailUrl as string}
          alt={item.name}
          className="h-11 w-11 rounded-chip bg-paper object-contain ring-1 ring-line"
        />
      )}
      {showEmptyTile && (
        <div
          className="h-11 w-11 rounded-chip bg-paper-3 ring-1 ring-line"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

/**
 * A draft whose mockups are still rendering cannot be approved: the
 * publish would go out with only the finished previews and the listing
 * would be missing colours. The queue polls while renders are pending,
 * so the row unlocks on its own (the API refuses it too, R-#5).
 */
function hasPendingMockups(item: ReviewQueueItem): boolean {
  return item.mockupSummary.pending > 0;
}

/**
 * Review queue for MCP-created drafts: approve (single sync / batch
 * job), retry failed publishes, discard, and jump into the Creator.
 * Rendered inside the SellerShell, which owns the header and navigation.
 */
export function ReviewPage() {
  const { t, i18n } = useTranslation();
  const queue = useReviewQueue();
  const approve = useApproveReviewDesigns();
  const discard = useDiscardReviewDesigns();
  const retry = useRetryReviewDesigns();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rowMessages, setRowMessages] = useState<Record<string, string>>({});
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [batchResults, setBatchResults] = useState<ReviewActionItem[]>([]);
  const job = useReviewJob(activeJobId);

  // Accumulates design names so the result table can label rows that
  // already left the queue.
  const namesRef = useRef<Map<string, string>>(new Map());
  const items = queue.data?.data ?? [];
  for (const item of items) {
    namesRef.current.set(item.id, item.name);
  }

  const needingReview = items.filter(
    (item) => item.reviewState === 'NEEDS_REVIEW' && !hasPendingMockups(item)
  );
  const allSelected =
    needingReview.length > 0 && selected.size === needingReview.length;
  const jobStatus = job.data?.job.status;
  const jobRunning =
    activeJobId !== null &&
    (jobStatus === undefined ||
      jobStatus === 'QUEUED' ||
      jobStatus === 'RUNNING');
  const actionPending =
    approve.isPending || retry.isPending || discard.isPending;

  const setRowMessage = (designId: string, message: string | null) => {
    setRowMessages((previous) => {
      const next = { ...previous };
      if (message === null) {
        delete next[designId];
      } else {
        next[designId] = message;
      }
      return next;
    });
  };

  /**
   * Inline row text for a failed action: a coded 409 carries its own
   * reason (mockups still rendering), a bare CAS conflict stays the
   * "changed elsewhere" line, everything else falls back to the
   * generic mapping.
   */
  const describeRowError = (error: unknown): string => {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      const code = getApiErrorCode(error);
      return (
        (code !== 'review_conflict' ? translateErrorCode(code) : null) ??
        t('seller.review.conflict')
      );
    }
    return describeApiError(error);
  };

  const toggleSelected = (designId: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(designId)) {
        next.delete(designId);
      } else {
        next.add(designId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(
      allSelected ? new Set() : new Set(needingReview.map((item) => item.id))
    );
  };

  const runSingle = async (
    designId: string,
    action: typeof approve | typeof retry | typeof discard
  ) => {
    setRowMessage(designId, null);
    try {
      const result = await action.mutateAsync([designId]);
      const item = result.items[0];
      if (item && !item.ok && item.error) {
        // Sync publish failure: the row stays (PUBLISH_FAILED) and the
        // refreshed queue shows the mapped reason; surface it inline
        // too so the feedback is immediate.
        setRowMessage(
          designId,
          translateErrorCode(item.error.code) ?? item.error.message
        );
      }
    } catch (error) {
      setRowMessage(designId, describeRowError(error));
    }
  };

  const runBatchApprove = async () => {
    const designIds = [...selected];
    if (designIds.length === 0) {
      return;
    }
    setBatchResults([]);
    setActiveJobId(null);
    try {
      const result = await approve.mutateAsync(designIds);
      setBatchResults(result.items);
      setActiveJobId(result.jobId);
      setSelected(new Set());
    } catch (error) {
      const singleId = designIds.length === 1 ? designIds[0] : undefined;
      if (
        singleId !== undefined &&
        axios.isAxiosError(error) &&
        error.response?.status === 409
      ) {
        setRowMessage(singleId, describeRowError(error));
      } else {
        setBatchResults([
          {
            designId: designIds.join(', '),
            ok: false,
            error: { code: 'request_failed', message: describeApiError(error) },
          },
        ]);
      }
    }
  };

  const dismissResults = () => {
    setActiveJobId(null);
    setBatchResults([]);
  };

  // Persistent per-item result table: immediate conflicts from the
  // batch response + the job report filling in incrementally per poll.
  const resultRows: ReviewActionItem[] = [
    ...batchResults,
    ...(job.data?.items ?? []).map((item) => ({
      designId: item.designId ?? `#${item.index + 1}`,
      ok: item.ok,
      error: item.error,
    })),
  ];
  const showResults = resultRows.length > 0 || activeJobId !== null;

  // Progress line next to the running title: the summary once the job
  // reports one, the report count before that, nothing until the first poll.
  let progress = '';
  if (job.data?.job.summary) {
    progress = t('seller.review.results.progressDone', {
      done: job.data.job.summary.succeeded + job.data.job.summary.failed,
      total: job.data.job.summary.total,
    });
  } else if (job.data) {
    progress = t('seller.review.results.progressReported', {
      reported: job.data.items.length,
      total: job.data.job.itemCount,
    });
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <PageHeader
        eyebrow={t('seller.review.eyebrow')}
        title={t('seller.review.title')}
        action={
          <>
            {queue.isFetching && !queue.isLoading && (
              <span
                role="status"
                className="flex items-center gap-1 text-xs text-mute"
              >
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                {t('seller.review.refreshing')}
              </span>
            )}
            <Button
              variant="outline"
              aria-pressed={allSelected}
              onClick={toggleSelectAll}
              disabled={needingReview.length === 0 || jobRunning}
            >
              {t('seller.review.selectAll')}
            </Button>
            <Button
              variant="primary"
              onClick={runBatchApprove}
              disabled={selected.size === 0 || jobRunning || actionPending}
            >
              {jobRunning
                ? t('seller.review.batchRunning')
                : t('seller.review.approveSelected', { count: selected.size })}
            </Button>
          </>
        }
      />

      <p className="max-w-prose text-sm text-mute">
        {t('seller.review.intro')}
      </p>

      {/* Batch job progress + persistent per-item result table */}
      {showResults && (
        <Card data-testid="batch-results" padding="sm">
          <CardHeader className="mb-2">
            <CardTitle className="flex items-center gap-2">
              {jobRunning ? (
                <>
                  <Loader2
                    className="h-4 w-4 animate-spin text-primary"
                    aria-hidden="true"
                  />
                  {t('seller.review.results.publishing')}
                  {progress && (
                    <span className="font-sans text-sm font-normal text-mute">
                      {progress}
                    </span>
                  )}
                </>
              ) : (
                t('seller.review.results.title')
              )}
            </CardTitle>
            {!jobRunning && (
              <Button variant="ghost" size="sm" onClick={dismissResults}>
                {t('seller.review.results.dismiss')}
              </Button>
            )}
          </CardHeader>
          {resultRows.length === 0 ? (
            <p className="text-sm text-mute">
              {t('seller.review.results.waiting')}
            </p>
          ) : (
            <table className="w-full text-sm text-ink">
              <TableHead>
                <tr>
                  <TableHeaderCell className="px-0 pr-4 py-1">
                    {t('seller.review.results.columnDesign')}
                  </TableHeaderCell>
                  <TableHeaderCell className="px-0 pr-4 py-1">
                    {t('seller.review.results.columnResult')}
                  </TableHeaderCell>
                  <TableHeaderCell className="px-0 py-1">
                    {t('seller.review.results.columnDetails')}
                  </TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                {resultRows.map((row, index) => (
                  <tr key={`${row.designId}-${index}`}>
                    <TableCell className="px-0 pr-4 py-1.5 text-ink-2">
                      {namesRef.current.get(row.designId) ?? row.designId}
                    </TableCell>
                    <TableCell className="px-0 pr-4 py-1.5">
                      {row.ok ? (
                        <span className="flex items-center gap-1 text-success">
                          <CheckCircle2
                            className="h-4 w-4"
                            aria-hidden="true"
                          />
                          {t('seller.review.results.published')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-danger">
                          <XCircle className="h-4 w-4" aria-hidden="true" />
                          {t('seller.review.results.failed')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="px-0 py-1.5 text-mute">
                      {row.error
                        ? (translateErrorCode(row.error.code) ??
                          row.error.message)
                        : t('seller.review.results.noDetails')}
                    </TableCell>
                  </tr>
                ))}
              </TableBody>
            </table>
          )}
        </Card>
      )}

      {/* Queue states */}
      {queue.isLoading ? (
        <div data-testid="review-skeleton">
          <LoadingState label={t('common.loading')} />
        </div>
      ) : queue.isError ? (
        <Card className="flex flex-col items-center gap-2 text-center text-danger">
          <AlertTriangle className="h-6 w-6" aria-hidden="true" />
          <p className="font-medium">{t('seller.review.error.title')}</p>
          <p className="text-sm">{describeApiError(queue.error)}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => queue.refetch()}
          >
            {t('seller.review.error.retry')}
          </Button>
        </Card>
      ) : items.length === 0 ? (
        <div data-testid="review-empty">
          <EmptyState
            icon={<CheckCircle2 className="h-6 w-6 text-success" />}
            title={t('seller.review.empty.title')}
            description={t('seller.review.empty.text')}
          />
        </div>
      ) : (
        <Table>
          <TableHead>
            <tr>
              <TableHeaderCell className="w-11">
                <span className="sr-only">
                  {t('seller.review.table.select')}
                </span>
              </TableHeaderCell>
              <TableHeaderCell>
                {t('seller.review.table.design')}
              </TableHeaderCell>
              <TableHeaderCell>
                {t('seller.review.table.status')}
              </TableHeaderCell>
              <TableHeaderCell>
                {t('seller.review.table.created')}
              </TableHeaderCell>
              <TableHeaderCell className="text-right">
                {t('seller.review.table.actions')}
              </TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const failed = item.reviewState === 'PUBLISH_FAILED';
              // Renders still running: approving now would publish a
              // listing without its missing previews, so the row waits.
              const pendingMockups = hasPendingMockups(item);
              return (
                <TableRow key={item.id}>
                  <TableCell className="w-11">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      aria-label={t('seller.review.item.select', {
                        name: item.name,
                      })}
                      checked={selected.has(item.id)}
                      onChange={() => toggleSelected(item.id)}
                      disabled={
                        item.reviewState !== 'NEEDS_REVIEW' ||
                        jobRunning ||
                        pendingMockups
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <MockupStrip item={item} />
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-ink">
                          {item.name}
                        </p>
                        <p className="truncate font-mono text-xs text-mute">
                          {item.products
                            .map((product) => product.name)
                            .join(', ')}
                        </p>
                        {failed && item.publishFailReason && (
                          <p className="mt-1 text-xs text-danger">
                            {describePublishFailReason(item.publishFailReason)}
                          </p>
                        )}
                        {pendingMockups && (
                          <p
                            data-testid={`pending-hint-${item.id}`}
                            className="mt-1 text-xs text-mute"
                          >
                            {t('seller.review.item.pendingHint')}
                          </p>
                        )}
                        {rowMessages[item.id] && (
                          <p
                            data-testid={`row-message-${item.id}`}
                            className="mt-1 flex items-center gap-1 text-xs text-warning"
                          >
                            <AlertTriangle
                              className="h-3 w-3"
                              aria-hidden="true"
                            />
                            {rowMessages[item.id]}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusPill variant={failed ? 'danger' : 'warning'}>
                      {failed
                        ? t('seller.review.item.publishFailed')
                        : t('seller.review.item.needsReview')}
                    </StatusPill>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-mute">
                    {formatDate(item.createdAt, i18n.language)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        title={t('seller.review.item.openInCreator')}
                      >
                        <Link to={`/creator/${item.id}`}>
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                          {t('seller.review.item.edit')}
                        </Link>
                      </Button>
                      {failed ? (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => runSingle(item.id, retry)}
                          disabled={
                            actionPending || jobRunning || pendingMockups
                          }
                          title={
                            pendingMockups
                              ? t('seller.review.item.pendingHint')
                              : undefined
                          }
                        >
                          <RefreshCw className="h-4 w-4" aria-hidden="true" />
                          {t('seller.review.item.retry')}
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => runSingle(item.id, approve)}
                          disabled={
                            actionPending || jobRunning || pendingMockups
                          }
                          title={
                            pendingMockups
                              ? t('seller.review.item.pendingHint')
                              : undefined
                          }
                        >
                          {t('seller.review.item.approve')}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-danger hover:text-danger"
                        onClick={() => runSingle(item.id, discard)}
                        aria-label={t('seller.review.item.discardAria', {
                          name: item.name,
                        })}
                        disabled={actionPending || jobRunning}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                        {t('seller.review.item.discard')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
